import React, { useState } from 'react';
import { Flex, Gutter, Title, GridBag, BodyText, RadioButton } from '@gaiads/telia-react-component-library';
import { useTranslation } from 'react-i18next';
import EditableItem from '../../../../shared/EditableItem/EditableItem';
import { AdminProductTypeEnum, LocaleResponse } from '@cdaas/api-clients/dist/product-service-openapi';

import { updateEntityByIndex } from '../../../../utils/entities';
import { Errors } from '../utils';

interface BasicDataProps {
  title: string;
  type: AdminProductTypeEnum | undefined;
  name: string | undefined;
  nameTranslations: { localeId: number; translation: string }[] | undefined;
  locale: LocaleResponse;
  sku: string | undefined;
  brand: string | undefined;
  handleTypeChange: (type: AdminProductTypeEnum) => void;
  handleNameChange: (name: string) => void;
  handleNameTranslationsChange: (translations: { localeId: number; translation: string }[]) => void;
  handleBrandChange: (brand: string) => void;
  nameError: Errors['name'];
  skuError: Errors['sku'];
  brandError: Errors['brand'];
}

function BasicData(props: BasicDataProps) {
  const {
    title,
    type,
    name,
    nameTranslations,
    locale,
    sku,
    brand,
    handleTypeChange,
    handleNameChange,
    handleNameTranslationsChange,
    handleBrandChange,
    nameError,
    skuError,
    brandError,
  } = props;
  const { t } = useTranslation('productscreen');

  const [nameReferenceInputOpen, setNameReferenceInputOpen] = useState<boolean>(false);

  const handleNameTranslationChange = (nameTranslation: string) => {
    const index = (nameTranslations || []).findIndex(translation => translation.localeId === locale.id);
    if (index !== -1 && nameTranslations) {
      const updatedTranslation = { ...nameTranslations[index], translation: nameTranslation };
      const updatedTranslations = updateEntityByIndex(nameTranslations, index, updatedTranslation);
      handleNameTranslationsChange(updatedTranslations);
    }
  };

  const currentNameTranslation = nameTranslations?.find(translation => translation.localeId === locale.id)?.translation;

  return (
    <Flex directionColumn>
      <Title size="md">{title}</Title>
      <Gutter />
      <BodyText color="gray400">{t('basic_data_section.info')}</BodyText>
      <Gutter />

      <GridBag>
        <GridBag.Item md={4}>
          <EditableItem
            divProps={{
              padding: { right: 'md' },
            }}
          >
            <EditableItem.Input
              value={currentNameTranslation}
              edit={true}
              inputProps={{
                name: 'nameTranslation',
                onBlur: (e: React.FocusEvent<HTMLInputElement>) => handleNameTranslationChange(e.target.value),
                placeholder: t('basic_data_section.name_translation_placeholder', { language: locale.name.split(' ')[0] }),
                label: t('basic_data_section.name_translation_label'),
                onKeyDown: () => undefined,
              }}
            />
            <Gutter size="sm" />
            <EditableItem.Input
              value={name}
              edit={nameReferenceInputOpen}
              isReference={true}
              focusOnEdit={true}
              inputProps={{
                name: 'name',
                onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                  handleNameChange(e.target.value);
                  setNameReferenceInputOpen(false);
                },
                placeholder: t('basic_data_section.name_reference_placeholder'),
                label: t('basic_data_section.name_reference_label'),
                onKeyDown: () => undefined,
                required: true,
                errorText: nameError,
              }}
              editIconProps={{
                'data-testid': `name-edit`,
                onClick: () => setNameReferenceInputOpen(true),
              }}
            />
          </EditableItem>
        </GridBag.Item>
        <GridBag.Item md={4}>
          <EditableItem
            divProps={{
              padding: { right: 'md' },
            }}
          >
            <EditableItem.Input
              value={brand}
              edit={true}
              inputProps={{
                name: 'brand',
                onBlur: (event: React.FocusEvent<HTMLInputElement>) => handleBrandChange(event.target.value),
                placeholder: t('basic_data_section.brand_placeholder'),
                label: t('basic_data_section.brand_label'),
                onKeyDown: () => undefined,
                required: true,
                errorText: brandError,
              }}
            />
          </EditableItem>
        </GridBag.Item>
        <GridBag.Item md={4}>
          <EditableItem>
            <EditableItem.Input
              value={sku}
              edit={true}
              inputProps={{
                name: 'sku',
                // Currently SKU can't be changed - should it be possible? In that case implement handleSkuChange
                //onBlur: (e: React.FocusEvent<HTMLInputElement>) => handleSkuChange(e.target.value),
                placeholder: t('basic_data_section.sku_placeholder'),
                label: t('basic_data_section.sku_label'),
                onKeyDown: () => undefined,
                disabled: true,
                errorText: skuError,
              }}
            />
          </EditableItem>
        </GridBag.Item>
      </GridBag>
      <Gutter size="xlg" />
      <RadioButton
        name={'product-type-hardware-checkbox'}
        label={
          <>
            {t('basic_data_section.product_type_hardware')}{' '}
            <span style={{ fontStyle: 'italic' }}>{t('basic_data_section.product_type_hardware_info')}</span>
          </>
        }
        checked={type === AdminProductTypeEnum.Hardware}
        onChange={() => handleTypeChange(AdminProductTypeEnum.Hardware)}
      />
      <Gutter size="sm" />
      <RadioButton
        name={'product-type-service-checkbox'}
        label={
          <>
            {t('basic_data_section.product_type_service')}{' '}
            <span style={{ fontStyle: 'italic' }}>{t('basic_data_section.product_type_service_info')}</span>
          </>
        }
        checked={type === AdminProductTypeEnum.Service}
        onChange={() => handleTypeChange(AdminProductTypeEnum.Service)}
      />
      <Gutter size="sm" />
    </Flex>
  );
}

export default React.memo(BasicData);
